import apiAxios from './apiAxios';
import { debounce } from './debounce';

// Permission-free employee picker endpoint (same data + employee_type/q params
// as active_list, but never trips CanCanCan 403 for users without the Employee
// read permission). Drives all sales/csd/eng employee selects below.
const employeUrl = '/organization/employees/public_list?';

const salesEmployeesUrl = `${employeUrl}employee_type=sales`;
const csdEmployeesUrl = `${employeUrl}employee_type=csd`;
const engEnployeesUrl = `${employeUrl}employee_type=engineering`;

// Vietnamese names are stored with diacritics but people type without them. The
// picker endpoint folds accents server-side (Organization::Employee.search_name),
// so folding both sides here too keeps this client-side pass from throwing away
// rows the server just decided were matches.
const fold = (value) => (value || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/đ/g, 'd')
  .replace(/Đ/g, 'D')
  .toLowerCase();

export const filterOptions = async (url, inputValue = "", justShowName = false, enrichedData = false) => {
  try {
    const response = await apiAxios.get(`${url}&page=1&per_page=30&q=${encodeURIComponent(inputValue)}`);
    const { data } = response;

    if (!Array.isArray(data)) return [];

    return data
      .map(item => {
        const attr = item.attributes || item;

        // Return enriched data for professional select components
        if (enrichedData) {
          return {
            value: justShowName ? attr.name : attr.id,
            label: attr.name,
            // full_name / first_name / last_name feed resolvePersonName, which is
            // what makes an option read "ABZ — Anthony Braganza" instead of a bare
            // three-letter code nobody can place.
            full_name: attr.full_name,
            first_name: attr.first_name,
            last_name: attr.last_name,
            employeeCode: attr.code || attr.employee_code,
            email: attr.email,
            department: attr.department_name || attr.department?.name,
            position: attr.title || attr.position_group?.title,
            location: attr.site_name || attr.site?.name,
            status: attr.status || 'active',
            avatarUrl: attr.avatar_url,
            // Keep raw data for additional use
            rawData: attr
          };
        }

        // Return simple format for backward compatibility
        return {
          value: justShowName ? attr.name : attr.id,
          label: attr.name
        };
      })
      // The server has already filtered on `q`; this pass only guards against a
      // looser server match. It used to test the CODE alone, so typing a full
      // name ("Thắng") returned rows from the API and then dropped every one of
      // them — the picker looked empty for the most natural way to search.
      .filter((option) => {
        const needle = fold(inputValue.trim());
        if (!needle) return Boolean(option.label);

        return [option.label, option.full_name, option.email, option.employeeCode]
          .some((field) => fold(field).includes(needle));
      });
  } catch (error) {
    console.error("filterOptions error:", error);
    return [];
  }
};

/**
 * Enriched variant: carries full name, email, department, site and title so
 * EmployeeSelectField can render a real person card per option. Use this
 * wherever the picker is the place someone identifies a colleague; the plain
 * `activeEmployeesDebounce` stays for callers that only need a code.
 */
export const activeEmployeesEnrichedDebounce = debounce((inputValue, callback) => {
  filterOptions(employeUrl, inputValue, false, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const activeEmployeesDebounce = debounce((inputValue, callback) => {
  filterOptions(employeUrl, inputValue).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const activeEmployeesByNameDebounce = debounce((inputValue, callback) => {
  filterOptions(employeUrl, inputValue, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);


export const salesEmployeesDebounce = debounce((inputValue, callback) => {
  filterOptions(salesEmployeesUrl, inputValue, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const salesEmployeesByNameDebounce = debounce((inputValue, callback) => {
  filterOptions(salesEmployeesUrl, inputValue, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

// Enhanced versions with enriched data for professional select components
export const salesEmployeesEnrichedDebounce = debounce((inputValue, callback) => {
  filterOptions(salesEmployeesUrl, inputValue, false, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const salesEmployeesByNameEnrichedDebounce = debounce((inputValue, callback) => {
  filterOptions(salesEmployeesUrl, inputValue, true, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const csdEmployeesDebounce = debounce((inputValue, callback) => {
  filterOptions(csdEmployeesUrl, inputValue).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const csdEmployeesByNameDebounce = debounce((inputValue, callback) => {
  filterOptions(csdEmployeesUrl, inputValue, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const engEmployeesDebounce = debounce((inputValue, callback) => {
  filterOptions(engEnployeesUrl, inputValue).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

export const engEmployeesByNameDebounce = debounce((inputValue, callback) => {
  filterOptions(engEnployeesUrl, inputValue, true).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);


export const buildDepartmentLabel = (attr) => {
  const name   = attr.name        || '';
  const parent = attr.parent_name || '';
  return parent ? `${parent} / ${name}` : name;
};

// Transform flat department list → React Select grouped options.
// Departments without a parent are placed in their own single-item group
// so the parent name always appears as a visible header.
export const buildDepartmentOptions = (rows) => {
  const groups = {};

  (rows || []).forEach((item) => {
    const attr  = item.attributes || item;
    const group = attr.parent_name || attr.name || 'General';

    // Count names within this group to detect duplicates
    if (!groups[group]) groups[group] = { items: [], nameCount: {} };
    const name = attr.name || '';
    groups[group].nameCount[name] = (groups[group].nameCount[name] || 0) + 1;
    groups[group].items.push(attr);
  });

  return Object.entries(groups).map(([label, { items, nameCount }]) => ({
    label,
    options: items.map((attr) => {
      const name      = attr.name      || '';
      const siteName  = attr.site_name || '';
      // Show site when the same name appears more than once in the group
      const isDuplicate = nameCount[name] > 1;
      const optLabel  = isDuplicate && siteName ? `${name} (${siteName})` : name;
      return { value: attr.id, label: optLabel };
    }),
  }));
};

const fetchDepartments = async (inputValue, siteId = null) => {
  try {
    const base = siteId
      ? `/organization/sites/${siteId}/departments`
      : '/organization/departments';
    const response = await apiAxios.get(`${base}?page=1&per_page=100&name_cont=${inputValue}`);
    return response.data?.rows || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const makeDepartmentLoader = (getSiteId) =>
  debounce((inputValue, callback) => {
    fetchDepartments(inputValue, getSiteId?.())
      .then(buildDepartmentOptions)
      .then(callback);
  }, 300);

export const departmentsDebounce = debounce((inputValue, callback) => {
  fetchDepartments(inputValue)
    .then(buildDepartmentOptions)
    .then(callback);
}, 300);

const filterSiteOptions = async (inputValue) => {
  try {
    const response = await apiAxios.get(`/organization/sites?page=1&per_page=50&name_cont=${inputValue}`);
    const { rows: data } = response.data;

    const options = data && data.map((item) => {
      return { value: item.attributes.id, label: item.attributes.name };
    });

    return options.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()));
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const sitesDebounce = debounce((inputValue, callback) => {
  filterSiteOptions(inputValue).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

const filterEmpEmailOptions = async (inputValue) => {
  try {
    const response = await apiAxios.get(`${employeUrl}&page=1&per_page=30&q=${inputValue}`);
    const { data } = response;

    const options = data && data.map((item) => {
      return { value: item.attributes.email, label: item.attributes.email };
    });

    return options.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()));
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const employeesEmailDebounce = debounce((inputValue, callback) => {
  filterEmpEmailOptions(inputValue).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

const filterEmpEmailWithNameOptions = async (inputValue) => {
  try {
    const response = await apiAxios.get(`${employeUrl}&page=1&per_page=30&q=${encodeURIComponent(inputValue)}`);
    const { data } = response;
    if (!Array.isArray(data)) return [];
    return data
      .map(item => {
        const attr = item.attributes || item;
        return { value: attr.email, label: attr.name, email: attr.email, name: attr.name };
      })
      .filter(o => o.value && (
        o.label.toLowerCase().includes(inputValue.trim().toLowerCase()) ||
        o.email.toLowerCase().includes(inputValue.trim().toLowerCase())
      ));
  } catch (error) {
    console.error("filterEmpEmailWithNameOptions error:", error);
    return [];
  }
};

export const employeesEmailWithNameDebounce = debounce((inputValue, callback) => {
  filterEmpEmailWithNameOptions(inputValue).then(callback);
}, 300);